﻿﻿/*
  Shopify Store Locator
  ---------------------
  - Les IDs du DOM sont générés à partir du `sectionId` Liquid.
  - Les classes CSS générées ici sont stylées dans `assets/store-locator.css`.
  - Flux : chargement des scripts -> init map/libs -> liaison UI -> chargement des magasins -> recherche.
*/

document.addEventListener('DOMContentLoaded', () => {
  const config = window.storeLocatorConfig;
  if (!config) return;

  // ---------------------------------------------------------------------------
  // 1) Configuration + références DOM
  // ---------------------------------------------------------------------------
  const { sectionId, jsonUrl, apiKey, ownStoreIcon } = config;
  const i18n = config.i18n || {};
  const sectionRoot = document.getElementById(`store-locator-${sectionId}`);
  const elements = {
    map: document.getElementById(`google-map-${sectionId}`),
    list: document.getElementById(`store-list-${sectionId}`),
    searchBtn: document.getElementById(`search-btn-${sectionId}`),
    geoBtn: document.getElementById(`geolocation-btn-${sectionId}`),
    radius: document.getElementById(`radius-select-${sectionId}`),
    mobileRadiusBtn: document.getElementById(`mobile-radius-btn-${sectionId}`),
    overlay: document.getElementById(`map-overlay-${sectionId}`),
    overlayGeo: document.getElementById(`overlay-geoloc-btn-${sectionId}`),
    overlaySearch: document.getElementById(`overlay-search-btn-${sectionId}`),
    input: document.getElementById(`location-input-${sectionId}`)
  };
  if (!elements.map || !elements.list) return;

  // ---------------------------------------------------------------------------
  // 2) État d'exécution + constantes
  // ---------------------------------------------------------------------------
  const state = {
    map: null,
    infoWindow: null,
    markers: [],
    clusterer: null,
    stores: [],
    placeAutocomplete: null,
    selectedPlace: null,
    geocoder: null,
    lastLocation: null,
    AdvancedMarkerElement: null,
    PlaceAutocompleteElement: null,
    cameraAnimationToken: 0,
    cameraTimeoutId: null
  };

  const defaultCenter = { lat: 46.603354, lng: 1.888334 };
  const MAX_RADIUS_KM = 25;
  const CLUSTER_MAX_ZOOM = 12;
  const MARKER_FOCUS_ZOOM = 14;
  const MARKER_FOCUS_OFFSET_PX = {
    desktop: -72,
    desktopOwn: -100,
    mobile: -100,
    mobileOwn: -150
  };
  const CLUSTER_TIER_ZOOM = {
    danger: 12,
    canary: 13,
    electric: 14
  };
  const sanitizeSectionId = sectionId.replace(/[^a-zA-Z0-9]/g, '');
  const callbackName = `initMap_${sanitizeSectionId}`;

  // ---------------------------------------------------------------------------
  // 3) Primitives UI
  // ---------------------------------------------------------------------------
  const showMessage = (title, body) => {
    elements.list.innerHTML = `
      <div class="no-results">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
          <path d="M12 8V12M12 16H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <p>${title}</p>
        ${body ? `<p class="small">${body}</p>` : ''}
      </div>
    `;
  };

  const showLoading = (message = i18n.loading_stores) => {
    elements.list.innerHTML = `
      <div class="loading-placeholder">
        <div class="spinner"></div>
        <p>${message}</p>
      </div>
    `;
  };

  const toggleButtonLoading = (button, isLoading) => {
    if (!button) return;
    button.classList.toggle('loading', isLoading);
    button.disabled = isLoading;
    button.setAttribute('aria-busy', String(isLoading));
  };

  const setActionLoading = (buttons, isLoading, message) => {
    buttons.forEach((button) => toggleButtonLoading(button, isLoading));
    if (isLoading && message) showLoading(message);
  };

  const hideOverlay = () => {
    if (elements.overlay) {
      elements.overlay.classList.add('hidden');
    }
  };

  const getInputValue = () => {
    if (state.placeAutocomplete) {
      const directValue = state.placeAutocomplete.value;
      if (typeof directValue === 'string' && directValue.trim()) return directValue.trim();
      const attrValue = state.placeAutocomplete.getAttribute('value');
      if (attrValue && attrValue.trim()) return attrValue.trim();
    }
    return elements.input ? elements.input.value.trim() : '';
  };

  // ---------------------------------------------------------------------------
  // 4) Calculs + helpers de marqueurs
  // ---------------------------------------------------------------------------
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2
      + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  };

  const getClusterClass = (count) => {
    const sizeClass = count >= 20 ? 'cluster-marker--lg' : count >= 10 ? 'cluster-marker--md' : 'cluster-marker--sm';
    const colorClass = count >= 20
      ? 'cluster-marker--danger'
      : count >= 10
        ? 'cluster-marker--canary'
        : count >= 2
          ? 'cluster-marker--electric'
          : '';
    return `cluster-marker ${sizeClass} ${colorClass}`.trim();
  };

  // Boutiques Schmoove en propre : marqueur distinct et remontee en tete de liste
  const isOwnStore = (store) => Boolean(store && store.type === 'own');

  const createStoreMarkerContent = (isOwn) => {
    const container = document.createElement('div');
    container.className = isOwn ? 'store-marker store-marker--schmoove' : 'store-marker';
    // Boutique en propre : le pin est evide et recoit le logo rond Schmoove dans sa tete
    container.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" aria-hidden="true">
        <path d="M128 252.6C128 148.4 214 64 320 64C426 64 512 148.4 512 252.6C512 371.9 391.8 514.9 341.6 569.4C329.8 582.2 310.1 582.2 298.3 569.4C248.1 514.9 127.9 371.9 127.9 252.6zM320 320C355.3 320 384 291.3 384 256C384 220.7 355.3 192 320 192C284.7 192 256 220.7 256 256C256 291.3 284.7 320 320 320z"/>
        ${isOwn ? '<circle cx="320" cy="256" r="150" fill="#ffffff"/>' : ''}
        ${isOwn && ownStoreIcon ? `<image href="${ownStoreIcon}" x="170" y="106" width="300" height="300"/>` : ''}
      </svg>
    `;
    return container;
  };

  // ---------------------------------------------------------------------------
  // 5) Rendu (liste, marqueurs, infowindow)
  // ---------------------------------------------------------------------------
  const clearMarkers = () => {
    if (state.clusterer) {
      state.clusterer.clearMarkers();
      state.clusterer = null;
    }
    state.markers.forEach((marker) => {
      marker.map = null;
    });
    state.markers = [];
  };

  const projectLatLng = ({ lat, lng }, zoom) => {
    const scale = 256 * 2 ** zoom;
    const siny = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
    return {
      x: ((lng + 180) / 360) * scale,
      y: (0.5 - (Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI))) * scale
    };
  };

  const unprojectLatLng = ({ x, y }, zoom) => {
    const scale = 256 * 2 ** zoom;
    const lng = (x / scale) * 360 - 180;
    const mercatorY = 0.5 - (y / scale);
    const lat = (2 * Math.atan(Math.exp(mercatorY * 2 * Math.PI)) - (Math.PI / 2)) * (180 / Math.PI);
    return { lat, lng };
  };

  const getOffsetCenter = (position, offsetX, offsetY, zoom) => {
    const projected = projectLatLng(position, zoom);
    return unprojectLatLng({
      x: projected.x + offsetX,
      y: projected.y + offsetY
    }, zoom);
  };

  const toLatLngLiteral = (position) => {
    if (!position) return null;
    if (typeof position.lat === 'function' && typeof position.lng === 'function') {
      return { lat: position.lat(), lng: position.lng() };
    }
    return position;
  };

  const cancelCameraAnimation = () => {
    state.cameraAnimationToken += 1;
    if (state.cameraTimeoutId) {
      window.clearTimeout(state.cameraTimeoutId);
      state.cameraTimeoutId = null;
    }
  };

  const animateMapView = ({ center, zoom, onComplete }) => {
    if (!state.map) return;

    const map = state.map;
    cancelCameraAnimation();
    const targetCenter = toLatLngLiteral(center) || toLatLngLiteral(map.getCenter());
    const currentZoom = map.getZoom();
    const targetZoom = Number.isFinite(zoom) ? Math.max(1, Math.round(zoom)) : currentZoom;

    if (!targetCenter || !Number.isFinite(currentZoom) || !Number.isFinite(targetZoom)) return;

    if (currentZoom === targetZoom) {
      map.panTo(targetCenter);
      if (typeof onComplete === 'function') onComplete();
      return;
    }

    map.panTo(targetCenter);
    state.cameraTimeoutId = window.setTimeout(() => {
      state.cameraTimeoutId = null;
      map.setZoom(targetZoom);
      if (typeof onComplete === 'function') onComplete();
    }, 120);
  };

  const getMarkerPosition = (marker) => {
    if (!marker || !marker.position) return null;
    if (typeof marker.position.lat === 'function' && typeof marker.position.lng === 'function') {
      return { lat: marker.position.lat(), lng: marker.position.lng() };
    }
    return marker.position;
  };

  const focusMarkerOnMap = (marker, ownStore) => {
    const markerPosition = getMarkerPosition(marker);
    if (!markerPosition) return;

    const isMobile = window.innerWidth <= 1024;
    const offsetY = isMobile
      ? (ownStore ? MARKER_FOCUS_OFFSET_PX.mobileOwn : MARKER_FOCUS_OFFSET_PX.mobile)
      : (ownStore ? MARKER_FOCUS_OFFSET_PX.desktopOwn : MARKER_FOCUS_OFFSET_PX.desktop);
    const adjustedCenter = getOffsetCenter(markerPosition, 0, offsetY, MARKER_FOCUS_ZOOM);
    const currentZoom = state.map ? state.map.getZoom() : null;

    if (!Number.isFinite(currentZoom)) return;

    if (Math.round(currentZoom) === MARKER_FOCUS_ZOOM) {
      cancelCameraAnimation();
      state.map.panTo(adjustedCenter);
      return;
    }

    animateMapView({
      center: markerPosition,
      zoom: MARKER_FOCUS_ZOOM,
      onComplete: () => {
        if (!state.map) return;
        state.map.panTo(adjustedCenter);
      }
    });
  };

  const zoomToCluster = (cluster) => {
    if (!state.map || !cluster) return;

    const targetZoom = cluster.count >= 20
      ? CLUSTER_TIER_ZOOM.danger
      : cluster.count >= 10
        ? CLUSTER_TIER_ZOOM.canary
        : CLUSTER_TIER_ZOOM.electric;

    animateMapView({
      center: cluster.position,
      zoom: targetZoom
    });
  };

  const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  // Telephone et horaires proviennent du JSON, pas du theme : rien ne s'affiche sans donnee
  const buildStoreDetails = (store) => {
    const phone = store.phone
      ? `<p class="info-window__phone"><strong>${i18n.phone_label}: </strong><a href="tel:${store.phone.replace(/[^+\d]/g, '')}">${store.phone}</a></p>`
      : '';
    const hours = store.hours
      ? `<p class="info-window__hours-heading"><strong>${i18n.our_hours}</strong></p>
          <hr>
          <div class="info-window__hours-list">
            ${DAY_KEYS.map((day) => `<div class="info-window__hours-row"><span class="info-window__hours-day">${i18n[day]}</span><span class="info-window__hours-time">${store.hours[day] || i18n.closed}</span></div>`).join('')}
          </div>`
      : '';
    if (!phone && !hours) return '';
    return `<div class="info-window__hours">${phone}${phone && hours ? '<hr>' : ''}${hours}</div>`;
  };

  const openInfoWindow = (store, marker) => {
    const addressParts = [];
    if (store.address.line1) addressParts.push(store.address.line1);
    if (store.address.line2) addressParts.push(store.address.line2);
    const streetAddress = addressParts.join(', ');
    const cityLine = `${store.address.postal_code} ${store.address.city}`;
    const ownStore = isOwnStore(store);

    let content = `<div class="info-window">`;
    content += `
      <h3>${store.name}</h3>
      ${streetAddress ? `<p>${streetAddress}</p>` : ''}
      <p>${cityLine}</p>
      ${store.distance !== undefined ? `<p class="info-window__distance"><strong>${store.distance.toFixed(1)} km</strong> ${i18n.distance_from_position}</p>` : ''}
      <hr>
      ${buildStoreDetails(store)}
      <a href="https://www.google.com/maps/dir//${encodeURIComponent(store.formatted_address)}" target="_blank" rel="noopener noreferrer" class="info-window__navigate">
        ${i18n.get_directions}
      </a>
    `;
    content += `</div>`;

    state.infoWindow.setContent(content);
    state.infoWindow.open(state.map, marker);
    focusMarkerOnMap(marker, ownStore);
  };

  const renderResults = (stores) => {
    if (!stores.length) {
      showMessage(i18n.no_stores_found, i18n.increase_radius);
      return;
    }

    let html = `<div class="results-header"><strong>${stores.length}</strong> ${stores.length > 1 ? i18n.store_count_other : i18n.store_count_one}</div>`;

    stores.forEach((store, index) => {
      const addressParts = [];
      if (store.address.line1) addressParts.push(store.address.line1);
      if (store.address.line2) addressParts.push(store.address.line2);
      const streetAddress = addressParts.join(', ');
      const cityLine = `${store.address.postal_code} ${store.address.city}`;
      const distanceText = store.distance ? `${store.distance.toFixed(1)} km` : '';

      html += `
        <div class="store-item" data-store-index="${index}">
          <div class="store-item-header">
            <span class="store-number">${index + 1}</span>
            <div class="store-info">
              <h3 class="store-name">${store.name}</h3>
              ${distanceText ? `<div class="store-distance">${distanceText}</div>` : ''}
            </div>
          </div>
          <div class="store-address">
            ${streetAddress ? `<p>${streetAddress}</p>` : ''}
            <p>${cityLine}</p>
          </div>
          <div class="store-actions">
            <button class="view-on-map-btn" data-store-index="${index}">${i18n.view_on_map}</button>
            <a href="https://www.google.com/maps/dir//${encodeURIComponent(store.formatted_address)}" target="_blank" rel="noopener noreferrer" class="navigate-btn">${i18n.directions}</a>
          </div>
        </div>
      `;
    });

    elements.list.innerHTML = html;
    document.querySelectorAll(`#store-list-${sectionId} [data-store-index]`).forEach((node) => {
      node.addEventListener('click', (event) => {
        event.stopPropagation();
        const index = Number(node.dataset.storeIndex);
        if (state.markers[index]) {
          openInfoWindow(stores[index], state.markers[index]);
          if (window.innerWidth <= 1024) {
            elements.map.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      });
    });
  };

  const renderMarkers = (stores) => {
    clearMarkers();
    const markers = stores.map((store) => {
      const marker = new state.AdvancedMarkerElement({
        position: { lat: store.coordinates.lat, lng: store.coordinates.lng },
        map: state.map,
        title: store.name,
        content: createStoreMarkerContent(isOwnStore(store))
      });
      marker.addListener('click', () => openInfoWindow(store, marker));
      return marker;
    });

    const renderer = {
      render({ count, position }) {
        const clusterElement = document.createElement('div');
        clusterElement.className = getClusterClass(count);
        clusterElement.innerHTML = `<span>${count}</span>`;
        return new state.AdvancedMarkerElement({
          position,
          content: clusterElement,
          zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count
        });
      }
    };

    const algorithm = markerClusterer.SuperClusterAlgorithm
      ? new markerClusterer.SuperClusterAlgorithm({ maxZoom: CLUSTER_MAX_ZOOM })
      : undefined;

    state.clusterer = new markerClusterer.MarkerClusterer({
      map: state.map,
      markers,
      renderer,
      algorithm,
      onClusterClick: (_, cluster) => {
        zoomToCluster(cluster);
      }
    });
    state.markers = markers;
  };

  const fitMap = (stores, userLocation) => {
    if (!stores.length) {
      state.map.setCenter(defaultCenter);
      state.map.setZoom(6);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    if (userLocation) bounds.extend(userLocation);
    stores.forEach((store) => bounds.extend({ lat: store.coordinates.lat, lng: store.coordinates.lng }));
    state.map.fitBounds(bounds);
    const listener = google.maps.event.addListener(state.map, 'idle', () => {
      if (state.map.getZoom() > 15) state.map.setZoom(15);
      google.maps.event.removeListener(listener);
    });
  };

  // ---------------------------------------------------------------------------
  // 6) Actions de recherche + géolocalisation
  // ---------------------------------------------------------------------------
  const searchFromLocation = (location) => {
    state.lastLocation = location;
    const radiusValue = elements.radius ? elements.radius.value : String(MAX_RADIUS_KM);
    const radius = Math.min(parseFloat(radiusValue || String(MAX_RADIUS_KM)), MAX_RADIUS_KM);

    const nearby = state.stores
      .filter((store) => store.coordinates && store.coordinates.lat && store.coordinates.lng)
      .map((store) => ({
        ...store,
        distance: calculateDistance(location.lat(), location.lng(), store.coordinates.lat, store.coordinates.lng)
      }))
      .filter((store) => store.distance <= radius)
      .sort((a, b) => {
        const aOwn = isOwnStore(a);
        const bOwn = isOwnStore(b);
        if (aOwn !== bOwn) return aOwn ? -1 : 1;
        return a.distance - b.distance;
      });

    renderResults(nearby);
    renderMarkers(nearby);
    fitMap(nearby, location);
  };

  const performSearch = () => {
    if (!state.stores.length) return;

    if (state.selectedPlace && state.selectedPlace.location) {
      const selectedLocation = state.selectedPlace.location;
      setActionLoading([elements.searchBtn], true, i18n.searching_nearby);
      state.selectedPlace = null;
      hideOverlay();
      searchFromLocation(selectedLocation);
      setActionLoading([elements.searchBtn], false);
      return;
    }

    const address = getInputValue();
    if (!address) {
      showMessage(i18n.enter_address, i18n.also_use_location);
      return;
    }

    setActionLoading([elements.searchBtn], true, i18n.processing_address);
    state.geocoder.geocode({ address, componentRestrictions: { country: "FR" } }, (results, status) => {
      setActionLoading([elements.searchBtn], false);
      if (status === "OK" && results[0]) {
        hideOverlay();
        searchFromLocation(results[0].geometry.location);
      } else {
        showMessage(i18n.address_not_found, i18n.please_retry);
      }
    });
  };

  const useGeolocation = () => {
    if (!navigator.geolocation) {
      showMessage(i18n.geolocation_unavailable, i18n.enter_address_hint);
      return;
    }

    setActionLoading(
      [elements.geoBtn, elements.overlayGeo],
      true,
      i18n.locating
    );

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);
        hideOverlay();
        searchFromLocation(location);
        setActionLoading([elements.geoBtn, elements.overlayGeo], false);

        state.geocoder.geocode({ location }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const address = results[0].formatted_address;
            if (state.placeAutocomplete) {
              const shadowInput = state.placeAutocomplete.shadowRoot
                ? state.placeAutocomplete.shadowRoot.querySelector('input')
                : null;
              if (shadowInput) {
                shadowInput.value = address;
              } else {
                state.placeAutocomplete.value = address;
              }
            } else if (elements.input) {
              elements.input.value = address;
            }
          }
        });
      },
      () => {
        showMessage(i18n.location_error, i18n.enter_address_hint);
        setActionLoading([elements.geoBtn, elements.overlayGeo], false);
      }
    );
  };

  // ---------------------------------------------------------------------------
  // 7) Champs de saisie + liaison des événements UI
  // ---------------------------------------------------------------------------
  const setupAutocomplete = () => {
    if (!state.PlaceAutocompleteElement || !elements.input) return;

    const placeAutocomplete = new state.PlaceAutocompleteElement({
      componentRestrictions: { country: 'fr' }
    });
    elements.input.parentNode.replaceChild(placeAutocomplete, elements.input);
    state.placeAutocomplete = placeAutocomplete;

    placeAutocomplete.addEventListener('gmp-select', async ({ placePrediction }) => {
      setActionLoading([elements.searchBtn], true, i18n.processing_address);
      try {
        const place = placePrediction.toPlace();
        await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] });
        state.selectedPlace = place;
        performSearch();
      } catch (error) {
        setActionLoading([elements.searchBtn], false);
        showMessage(i18n.address_not_found, i18n.please_retry);
      }
    });
  };

  const bindUI = () => {
    if (elements.mobileRadiusBtn && elements.radius) {
      const radiusValues = ['5', '10', '15', '25'];
      let currentIndex = radiusValues.indexOf(elements.radius.value);
      if (currentIndex === -1) currentIndex = 3;

      elements.mobileRadiusBtn.addEventListener('click', () => {
        currentIndex = (currentIndex + 1) % radiusValues.length;
        elements.radius.value = radiusValues[currentIndex];
        const span = elements.mobileRadiusBtn.querySelector('span');
        if (span) span.textContent = i18n.radius_label.replace('{{ value }}', radiusValues[currentIndex]);
        if (state.lastLocation) searchFromLocation(state.lastLocation);
      });
    }

    if (elements.searchBtn) {
      elements.searchBtn.addEventListener('click', performSearch);
    }

    if (sectionRoot) {
      sectionRoot.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        const activeElement = document.activeElement;
        const placeAutocomplete = state.placeAutocomplete || sectionRoot.querySelector('gmp-place-autocomplete');

        if (placeAutocomplete) {
          let isInPlaceAutocomplete = false;
          if (activeElement === placeAutocomplete) isInPlaceAutocomplete = true;
          else if (placeAutocomplete.shadowRoot && placeAutocomplete.shadowRoot.contains(activeElement)) isInPlaceAutocomplete = true;
          else if (placeAutocomplete.contains && placeAutocomplete.contains(activeElement)) isInPlaceAutocomplete = true;
          else if (placeAutocomplete.shadowRoot) {
            const shadowInput = placeAutocomplete.shadowRoot.querySelector('input');
            if (shadowInput && (event.target === shadowInput || activeElement === shadowInput)) isInPlaceAutocomplete = true;
          }

          if (isInPlaceAutocomplete) {
            event.preventDefault();
            event.stopPropagation();
            performSearch();
          }
        } else if (elements.input && (activeElement === elements.input || event.target === elements.input)) {
          event.preventDefault();
          event.stopPropagation();
          performSearch();
        }
      }, true);
    }

    if (elements.radius) {
      elements.radius.addEventListener('change', () => {
        if (state.lastLocation) searchFromLocation(state.lastLocation);
      });
    }

    if (elements.geoBtn) elements.geoBtn.addEventListener('click', useGeolocation);

    if (elements.overlayGeo) {
      elements.overlayGeo.addEventListener('click', () => {
        useGeolocation();
      });
    }

    if (elements.overlaySearch) {
      elements.overlaySearch.addEventListener('click', () => {
        hideOverlay();
        setTimeout(() => {
          if (state.placeAutocomplete) {
            const shadowInput = state.placeAutocomplete.shadowRoot
              ? state.placeAutocomplete.shadowRoot.querySelector('input')
              : null;
            if (shadowInput) shadowInput.focus();
            else state.placeAutocomplete.focus();
          } else if (elements.input) {
            elements.input.focus();
          }
        }, 100);
      });
    }
  };

  // ---------------------------------------------------------------------------
  // 8) Données + initialisation des APIs Google
  // ---------------------------------------------------------------------------
  const loadStores = async () => {
    showLoading();
    try {
      const response = await fetch(jsonUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const stores = await response.json();
      state.stores = stores || [];
      showMessage(i18n.search_store, i18n.use_address_or_location);
    } catch (error) {
      showMessage(i18n.load_error, i18n.please_retry);
    }
  };

  window[callbackName] = async () => {
    const { Map, InfoWindow } = await google.maps.importLibrary('maps');
    const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');
    const { PlaceAutocompleteElement } = await google.maps.importLibrary('places');

    state.AdvancedMarkerElement = AdvancedMarkerElement;
    state.PlaceAutocompleteElement = PlaceAutocompleteElement;
    state.map = new Map(elements.map, {
      zoom: 6,
      center: defaultCenter,
      mapTypeControl: false,
      fullscreenControl: false,
      zoomControl: true,
      mapId: '4fa3f93ca6a5b995d60aa29a'
    });
    state.infoWindow = new InfoWindow({ disableAutoPan: true });
    state.geocoder = new google.maps.Geocoder();

    if (elements.radius) {
      Array.from(elements.radius.options).forEach((option) => {
        if (parseFloat(option.value) > MAX_RADIUS_KM) option.remove();
      });
      if (parseFloat(elements.radius.value || '0') > MAX_RADIUS_KM) {
        elements.radius.value = String(MAX_RADIUS_KM);
      }
    }

    setupAutocomplete();
    bindUI();
    await loadStores();
  };

  const loadScript = (src, id) => new Promise((resolve, reject) => {
    const existing = document.getElementById(id);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });

  const loadClusterer = () => {
    if (window.markerClusterer && window.markerClusterer.MarkerClusterer) {
      return Promise.resolve();
    }
    return loadScript(
      'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js',
      `marker-clusterer-${sanitizeSectionId}`
    );
  };

  const loadMaps = () => {
    if (window.google && window.google.maps) return Promise.resolve();
    const src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=${callbackName}&loading=async&v=weekly`;
    return loadScript(src, `google-maps-${sanitizeSectionId}`);
  };

  if (!window.googleMapsLoaded) {
    loadClusterer()
      .then(() => loadMaps())
      .catch(() => showMessage(i18n.map_load_error, i18n.please_retry));
    window.googleMapsLoaded = true;
  } else {
    loadClusterer()
      .then(() => {
        if (window[callbackName]) window[callbackName]();
      })
      .catch(() => showMessage(i18n.map_load_error, i18n.please_retry));
  }
});